import React, { useState, useEffect } from 'react';
import { nanoid } from '@reduxjs/toolkit';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';
import { Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useQuery } from '../../../utils/hooks';
import { fetchJob, fetchRecommendedJobs, fetchPublicJob } from '../../../actions/jobs';
import Tabs from '../../../Components/common/Tabs/Tabs';
import RecommendedJob from '../../../Components/common/Cards/RecomendedJobCard/RecomendedJobCard';
import QuickApply from '../../../Components/quickapply';
import api from '../../../api/apiSettings';
import JobCard from './JobCard';
import styles from './jobs.module.scss';
import JobCardSkeleton from '../../../Components/common/Skeletons/JobCardSkeleton';

const Container = styled('div')((props) => ({
  display: 'grid',
  grid: `"header header" auto
         "info info" auto
          "recommended recommended" auto`,
  gap: '20px 50px',
  width: '100%',
  padding: '10px 16px 16px',
  [props.theme.breakpoints.up('1020')]: {
    grid: `"header header" auto
         "info recommended" 1fr / 2fr 1fr `,
    padding: '10px 50px 50px'
  }
}));

const StyledJobInfoContainer = styled('div')(() => ({
  gridArea: 'info',
  height: 'min-content',
  display: 'flex',
  flexDirection: 'column'
}));

const StyledRecommendedContainer = styled('div')((props) => ({
  gridArea: 'recommended',
  borderTop: '1px solid #D1D3D4',
  borderLeft: 'none',
  padding: ' 20px 0 20px 0',
  [props.theme.breakpoints.up('1020')]: {
    borderLeft: '1px solid #D1D3D4',
    borderTop: 'none',
    padding: ' 10px 0 20px 35px'
  }
}));

const StyledRecommendedTitle = styled('h5')({
  fontSize: '14px',
  lineHeight: '17px',
  fontWeight: 325,
  color: '#003B58',
  margin: 0
});
const StyledRecommendedSubtitle = styled('h6')({
  fontSize: '12px',
  lineHeight: '14px',
  fontWeight: 300,
  color: '#77787B',
  margin: '0 0 20px'
});

const StyledRecommendedCard = styled(RecommendedJob)({
  marginBottom: '20px',
  ':last-of-type': {
    marginBottom: 0
  }
});

function Job() {
  const { id } = useParams();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const query = useQuery();
  const pendingFlag = useSelector((state) => state.jobs.pendingFlag);
  const { t } = useTranslation();

  const completedProfile = localStorage.getItem('userProfileCompleted') === 'true';

  const [counters, setCounters] = useState({});

  const baseTabs = [
    {
      title: t('dashboard.jobs.feed'),
      value: 'feed',
      badgeText: isAuthenticated ? counters?.TotalJobs : '',
      redirect: '/dashboard/jobs?tab=feed'
    }
  ];

  const authTabs = [
    {
      title: t('dashboard.jobs.applied'),
      value: 'applied',
      badgeText: counters?.AppliedJobs,
      redirect: '/dashboard/jobs?tab=applied'
    },
    {
      title: t('profile.tabs.saved'),
      value: 'saved',
      badgeText: counters?.SavedJobs,
      redirect: '/dashboard/jobs?tab=saved'
    }
  ];

  const getTabs = (loggedIn) => (loggedIn ? [...baseTabs, ...authTabs] : baseTabs);

  const tabs = getTabs(isAuthenticated);

  const dispatch = useDispatch();
  const job = useSelector((state) => state.job.response);
  const recommendedJobs = useSelector((state) => state.jobs.recommendedJobs);

  const applyHandler = () => {
    window.localStorage.setItem(`OpenedJobId`, JSON.stringify(Number(id)));
    if (isAuthenticated) {
      /* user logged in */
      navigate({
        pathname: `/dashboard/jobs/${id}`,
        search: `?action=apply`
      });
    } else {
      /* user logged out */
      navigate({
        pathname: `/jobs/${id}`,
        search: `?action=apply`
      });
    }
  };

  const getCounters = async () => {
    const res = await api.get(`v1/job/job-count`);
    setCounters({ ...res.data.Data });
  };

  const changeTab = (value) => navigate(value.redirect);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchJob(id));
      if (completedProfile) {
        dispatch(fetchRecommendedJobs());
      }
    } else {
      dispatch(fetchPublicJob(id));
    }

    getCounters();
  }, [isAuthenticated, id]);

  return (
    <>
      {query.get('action') === 'apply' && <QuickApply />}
      <Container>
        <Tabs tabs={tabs} currentTab={tabs[0]} setTab={changeTab} />
        <StyledJobInfoContainer>
          {job ? <JobCard job={job} applyHandler={applyHandler} /> : ''}
          <div role="presentation" onClick={() => navigate(-1)} className={styles.backBtn}>
            {t('common.general.back')}
          </div>
        </StyledJobInfoContainer>
        {isAuthenticated && completedProfile && !pendingFlag && (
          <StyledRecommendedContainer>
            <StyledRecommendedTitle>{t('profile.tabs.recommendedjobs')}</StyledRecommendedTitle>
            <StyledRecommendedSubtitle>{t('jobs.preferences')}</StyledRecommendedSubtitle>
            {recommendedJobs.map((item) => (
              <StyledRecommendedCard key={item.Id} job={item} />
            ))}
          </StyledRecommendedContainer>
        )}
        {isAuthenticated && completedProfile && pendingFlag && (
          <StyledRecommendedContainer>
            <Stack gap={2}>
              {Array.from({ length: 5 }, (v, k) => k).map((_) => (
                <JobCardSkeleton key={nanoid()} />
              ))}
            </Stack>
          </StyledRecommendedContainer>
        )}
      </Container>
    </>
  );
}

export default Job;
